import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { MessengerService } from '../messenger/messenger.service';

@Injectable()
export class UploaderService {

  chunkSize
  threadsQuantity
  file
  fileName
  aborted
  uploadedSize
  progressCache
  activeConnections
  parts
  uploadedParts
  fileId
  fileKey
  onProgressFn
  onFinishFn
  onErrorFn

  constructor(private http: HttpClient, private ms: MessengerService, @Inject('UPLOAD_OPTIONS') private options: any) {
    this.chunkSize = options.chunkSize || 1024 * 1024 * 5
    this.threadsQuantity = Math.min(options.threadsQuantity || 5, 15);
    this.file = options.file;
    this.fileName = options.fileName;
    this.aborted = false;
    this.uploadedSize = 0;
    this.progressCache = {};
    this.activeConnections = {};
    this.parts = [];
    this.uploadedParts = [];
    this.fileId = null;
    this.fileKey = null;
    this.onProgressFn = () => {
    }
    this.onFinishFn = () => {
    }
    this.onErrorFn = () => {
    }
  }

  public checkUpload() {
    const options = { headers: { 'authentication': this.ms.basicMessengerService.getAuthenticationHeader() } };
    return this.http.post(environment.apiBaseURL + '/videoUploader/alreadyExisting', { name: this.fileName }, options).toPromise().then((res: any) => {
      return res && res.meta && res.meta.reason && res.meta.reason === 'existing';
    });
  }

  public async start() {
    try {
      const videoInitializationUploadInput = { name: this.fileName }
      const options = { headers: { 'authentication': this.ms.basicMessengerService.getAuthenticationHeader() } };
      const AWSFileDataOutput: any = await this.http.post(environment.apiBaseURL + '/videoUploader/initializeMultipartUpload', videoInitializationUploadInput, options).toPromise();
      this.fileId = AWSFileDataOutput.fileId;
      this.fileKey = AWSFileDataOutput.fileKey;
      const partCount = Math.ceil(this.file.size / this.chunkSize)
      const AWSMultipartFileDataInput = { fileId: this.fileId, fileKey: this.fileKey, parts: partCount }

      const urlsResponse: any = await this.http.post(environment.apiBaseURL + '/videoUploader/getMultipartPreSignedUrls', AWSMultipartFileDataInput, options).toPromise();
      const newParts = urlsResponse.parts;
      this.parts.push(...newParts);
      this.sendNext();
      return this.fileKey;
    } catch (error) {
      return this.complete(error);
    }
  }

  public onProgress(onProgress) {
    this.onProgressFn = onProgress
    return this;
  }

  public onFinish(onFinish) {
    this.onFinishFn = onFinish
    return this;
  }

  private sendNext() {
    const activeConnections = Object.keys(this.activeConnections).length

    if (activeConnections >= this.threadsQuantity) {
      return
    }

    if (!this.parts.length) {
      return !activeConnections ? this.complete() : undefined;
    }

    const part = this.parts.shift();
    if (this.file && part) {
      const sentSize = (part.PartNumber - 1) * this.chunkSize;
      const chunk = this.file.slice(sentSize, sentSize + this.chunkSize);

      const sendChunkStarted = () => {
        return this.sendNext();
      };

      return this.sendChunk(chunk, part, sendChunkStarted).then(() => {
        return this.sendNext();
      }).catch((error) => {
        this.parts.push(part);
        return this.complete(error);
      })
    }
  }

  private complete(error?) {
    if (error && !this.aborted) {
      this.onErrorFn(error)
      return
    }

    if (error) {
      this.onErrorFn(error)
      return
    }

    try {
      return this.sendCompleteRequest();
    } catch (error) {
      this.onErrorFn(error)
    }
  }

  private sendCompleteRequest() {
    const options = { headers: { 'authentication': this.ms.basicMessengerService.getAuthenticationHeader() } };
    if (this.fileId && this.fileKey) {
      const videoFinalizationMultiPartInput = {
        fileId: this.fileId,
        fileKey: this.fileKey,
        parts: this.uploadedParts,
      }

      return this.http.post(environment.apiBaseURL + '/videoUploader/finalizeMultipartUpload', videoFinalizationMultiPartInput, options).toPromise().then(res => {
        this.onFinishFn(this.fileKey);
        return this.fileKey;
      });
    }
  }

  private sendChunk(chunk, part, sendChunkStarted) {
    return new Promise((resolve, reject) => {
      this.upload(chunk, part, sendChunkStarted).then((status) => {
        if (status !== 200) {
          reject(new Error('Failed chunk upload'))
          return
        }

        resolve(true);
      }).catch((error) => {
        reject(error)
      });
    });
  }

  private handleProgress(part, event) {
    if (this.file) {
      if (event.type === 'progress' || event.type === 'error' || event.type === 'abort') {
        this.progressCache[part] = event.loaded
      }

      if (event.type === 'uploaded') {
        this.uploadedSize += this.progressCache[part] || 0
        delete this.progressCache[part]
      }

      const inProgress = Object.keys(this.progressCache).map(Number).reduce((memo, id) => (memo += this.progressCache[id]), 0);

      const sent = Math.min(this.uploadedSize + inProgress, this.file.size)

      const total = this.file.size

      const percentage = Math.round((sent / total) * 100)

      this.onProgressFn({
        sent: sent,
        total: total,
        percentage: percentage,
      });
    }
  }

  private upload(file, part, sendChunkStarted) {
    return new Promise((resolve, reject) => {
      if (this.fileId && this.fileKey) {
        const xhr = (this.activeConnections[part.PartNumber - 1] = new XMLHttpRequest())

        sendChunkStarted()

        const progressListener = this.handleProgress.bind(this, part.PartNumber - 1)

        xhr.upload.addEventListener('progress', progressListener)

        xhr.addEventListener('error', progressListener)
        xhr.addEventListener('abort', progressListener)
        xhr.addEventListener('loadend', progressListener)

        xhr.open('PUT', part.signedUrl)

        xhr.onreadystatechange = () => {
          if (xhr.readyState === 4 && xhr.status === 200) {
            const ETag = xhr.getResponseHeader('ETag')

            if (ETag) {
              const uploadedPart = {
                PartNumber: part.PartNumber,
                ETag: ETag.replace(/\"/, ''),
              }

              this.uploadedParts.push(uploadedPart)

              resolve(xhr.status)
              delete this.activeConnections[part.PartNumber - 1]
            }
          }
        }

        xhr.onerror = (error) => {
          reject(error)
          delete this.activeConnections[part.PartNumber - 1]
        }

        xhr.onabort = () => {
          reject(new Error('Upload canceled by user'))
          delete this.activeConnections[part.PartNumber - 1]
        }

        xhr.send(file)
      }
    })
  }

  private onError(onError) {
    this.onErrorFn = onError
    return this;
  }

  private abort() {
    Object.keys(this.activeConnections)
      .map(Number)
      .forEach((id) => {
        this.activeConnections[id].abort()
      })

    this.aborted = true
  }
}
